import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton
} from '@mui/material';
import Icons from '../../components/icons';
import GradeRow from './GradeRow';
import {
  SectionAddContainer,
  SectionAddButton,
  AddGradeButtonDesktop,
  AddGradeButtonMobile,
  AddNewSectionTextField
} from './styled';
import { SchoolStructureData, Grade } from './types';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';
import formUtils from '../../utils/formUtils';

const NEW_GRADE_TEMPLATE: Grade = {
  name: '',
  displayName: '',
  divisions: [],
  board: ''
};
interface GradeSectionProps {
  formData: SchoolStructureData;
  handleAccordionChange: (
    panel: string
  ) => (_: React.SyntheticEvent, isExpanded: boolean) => void;
  control: Control<SchoolStructureData>;
  setValue: UseFormSetValue<SchoolStructureData>;
}

const GradeSection: React.FC<GradeSectionProps> = ({
  formData,
  handleAccordionChange,
  control,
  setValue
}) => {
  const [expandedSection, setExpandedSection] = useState<string | false>(false);
  const watch = control?._getWatch;
  const { errors } = control?._formState;

  useEffect(() => {
    const firstFormError = formUtils.findFirstFormError(errors);
    const expandGradeSection = firstFormError?.fieldPath.split('.')[1] || '';
    setExpandedSection(expandGradeSection);
  }, [errors]);

  const handleAddGrade = useCallback(
    (fieldPath: string) => {
      setValue(
        fieldPath as any,
        [...(watch(fieldPath as any) || []), NEW_GRADE_TEMPLATE],
        {
          shouldValidate: true
        }
      );
    },
    [setValue, watch]
  );

  const handleRemoveGrade = useCallback(
    (fieldPath: string, indexToRemove: number) => {
      const currentArray = watch(fieldPath as any) || [];
      if (!Array.isArray(currentArray)) return;

      const updatedArray = currentArray.filter(
        (_: any, index: number) => index !== indexToRemove
      );
      setValue(fieldPath as any, updatedArray, { shouldValidate: true });
    },
    [setValue, watch]
  );

  const handleNewSection = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (
      formData.newSectionName &&
      !formData.sections[formData.newSectionName]
    ) {
      setValue(
        `sections.${formData.newSectionName}`,
        { grades: [] },
        { shouldValidate: true }
      );
      setValue('newSectionName', '');
      handleAccordionChange('sections')(event, true);
      setExpandedSection(formData.newSectionName);
    }
  };

  const handleDeleteSection = useCallback(
    (sectionKey: string) => {
      const newSections = { ...formData.sections };
      delete newSections[sectionKey];
      setValue('sections', newSections, { shouldValidate: true });

      if (expandedSection === sectionKey) {
        setExpandedSection(false);
      }
    },
    [expandedSection, formData.sections, setValue]
  );

  return (
    <>
      <SectionAddContainer>
        <Controller
          name={'newSectionName'}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <AddNewSectionTextField
              {...field}
              fullWidth
              label='New Section Name'
              variant='outlined'
              error={!!error}
              helperText={error?.message || ''}
              onChange={(e) => {
                field.onChange(e.target.value);
              }}
              onBlur={(e) => {
                const trimmed = e.target.value.trim();
                field.onChange(trimmed);
                field.onBlur();
              }}
            />
          )}
        />
        <Controller
          name={'newSectionName'}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <SectionAddButton
              variant='contained'
              color='primary'
              onClick={handleNewSection}
              startIcon={<Icons.PlusWhite />}
              disabled={
                !formData.newSectionName ||
                !!formData.sections[formData.newSectionName] ||
                error
              }
              style={{ marginBottom: error ? '20px' : '0px' }}
            >
              Add Section
            </SectionAddButton>
          )}
        />
      </SectionAddContainer>

      {Object.entries(formData.sections).map(([sectionKey, sectionData]) => (
        <Accordion
          key={sectionKey}
          expanded={expandedSection === sectionKey}
          onChange={() => setExpandedSection(sectionKey)}
        >
          <AccordionSummary expandIcon={<Icons.CaretDown />}>
            <Box
              width='100%'
              display='flex'
              alignItems='center'
              justifyContent='space-between'
            >
              <Typography variant='h6'>{sectionKey}</Typography>
              <Box>
                <AddGradeButtonDesktop
                  variant='contained'
                  color='primary'
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddGrade(`sections.${sectionKey}.grades`);
                  }}
                  startIcon={<Icons.PlusWhite />}
                >
                  Add Grade
                </AddGradeButtonDesktop>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteSection(sectionKey);
                  }}
                  aria-label={`Delete section ${sectionKey}`}
                >
                  <Icons.Delete />
                </IconButton>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {sectionData.grades.map((grade, gradeIndex) => (
              <GradeRow
                key={gradeIndex}
                control={control}
                sectionKey={sectionKey}
                gradeIndex={gradeIndex}
                onRemove={(index) =>
                  handleRemoveGrade(`sections.${sectionKey}.grades`, index)
                }
                setValue={setValue}
              />
            ))}
            <AddGradeButtonMobile
              variant='contained'
              color='primary'
              onClick={(e) => {
                e.stopPropagation();
                handleAddGrade(`sections.${sectionKey}.grades`);
              }}
              startIcon={<Icons.PlusWhite />}
            >
              Add Grade
            </AddGradeButtonMobile>
          </AccordionDetails>
        </Accordion>
      ))}
    </>
  );
};

export default GradeSection;
